import React, { useEffect, useState } from "react";
import { Box, useTheme } from "@mui/material";
import { motion, AnimatePresence, type Variants } from "framer-motion";
import { useLocation } from "react-router";
import Sidebar from "../Navigation/Sidebar";
import Topbar from "../Navigation/Topbar";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Outlet } from "react-router";
import { useGetProfile } from "../../hooks/api/useProfile";
import { useDispatch } from "react-redux";
import { setProfileLoadError } from "../../store/slices/userSlice";

import { layout } from "../../theme/theme";
import NoStoreOnboardingDialog from "../Store/NoStoreOnboardingDialog";
import { captureAppException } from "../../Utils/appLogger";
import { trackClarityEvent } from "../../analytics/clarity";

const pageVariants: Variants = {
  initial: { opacity: 0, y: 8 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.35,
      ease: [0.25, 0.46, 0.45, 0.94] as const
    }
  },
  exit: { opacity: 0, y: -4, transition: { duration: 0.2 } }
};

const AppShell: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));
  const [showFlyOver, setShowFlyOver] = useState<boolean>(false);
  const dispatch = useDispatch();
  const getProfileMutation = useGetProfile();

  useEffect(() => {
    const loadProfile = async () => {
      try {
        await getProfileMutation.mutateAsync();
        trackClarityEvent("portal_ready");
      } catch (err) {
        captureAppException("AppShell: Failed to fetch user details", err);
        dispatch(setProfileLoadError(true));
      }
    };
    loadProfile();
  }, []);

  useEffect(() => {
    if (!matchMD) {
      setShowFlyOver(false);
    }
  }, [matchMD]);

  const contentBg = layout.contentBg;

  return (
    <Box sx={{ display: "flex", height: "100vh", bgcolor: contentBg }}>
      {!matchMD && <Sidebar />}

      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          bgcolor: contentBg,
          borderLeft: "none",
          outline: "none",
          boxShadow: "none"
        }}
      >
        <Topbar
          openFlyOver={matchMD ? () => setShowFlyOver(true) : undefined}
          showMenuIcon={matchMD}
        />

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            overflow: "auto",
            p: { xs: 2, sm: 3 },
            boxSizing: "border-box",
            bgcolor: contentBg,
            borderLeft: "none",
            outline: "none"
          }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              style={{ height: "100%", minHeight: 0 }}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </Box>
        {matchMD && (
          <Sidebar
            mobile
            open={showFlyOver}
            onClose={() => setShowFlyOver(false)}
            setFlyOver={setShowFlyOver}
          />
        )}
      </Box>
      <NoStoreOnboardingDialog />
    </Box>
  );
};

export default AppShell;
